import { LOCAL_STORAGE_COOKIES_ACCEPTED } from '@/constants/local-storage';

/**
 * Informational cookie notice (SCRUM-1954, decision of 20/10). Tracking is always on: GTM, PostHog, the
 * Yandex counter and the OpenAI Ads pixel load on arrival with Google Consent Mode already `granted`,
 * and nothing waits for a click. The banner only tells the visitor that continuing to browse means
 * agreeing to cookies, and links the privacy policy. The LGPD/GDPR risk of this model was accepted by the PO.
 *
 * The only state is "the notice was seen" (`npCookiesAccepted='true'`) — NOT a consent record, and no
 * tag may read it as one. See `docs/cookie-consent.md`.
 */

/**
 * Inline script for `_app.tsx`: Consent Mode `default` with every signal `granted`. It runs at HTML parse
 * time, before GTM/GA load, so no `wait_for_update` and no later `update` are needed. `gtag` stays a
 * top-level declaration on purpose — that is what makes it `window.gtag`.
 */
export const consentModeBootstrapScript = (): string => `
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('consent', 'default', {
    ad_storage: 'granted',
    analytics_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted'
  });
`;

/** Whether the visitor already dismissed the notice. Never throws (storage may be blocked). */
export const hasSeenCookieNotice = (): boolean => {
  try {
    return localStorage.getItem(LOCAL_STORAGE_COOKIES_ACCEPTED) === 'true';
  } catch {
    return false;
  }
};

/** Fired on `window` (same tab) by `markCookieNoticeSeen`. Other tabs learn through the `storage` event on `LOCAL_STORAGE_COOKIES_ACCEPTED`. */
export const COOKIE_NOTICE_SEEN_EVENT = 'np:cookie-notice-seen';

/**
 * Records that the notice was dismissed and tells this tab, so the banner closes whoever called it (its own
 * button, or the support chat opening). Never throws: when storage refuses, the banner reappears on the next page.
 */
export const markCookieNoticeSeen = (): void => {
  try {
    localStorage.setItem(LOCAL_STORAGE_COOKIES_ACCEPTED, 'true');
  } catch {
    // Private mode / quota: nothing to record.
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(COOKIE_NOTICE_SEEN_EVENT));
};
